import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Plus, Edit2, Trash2, Copy, Download, Upload, Play } from 'lucide-react'
import type { GameFile } from '@creanizer/types'

interface GameFileManagerProps {
  gameFiles: GameFile[]
  onCreateNew: () => void
  onEdit: (file: GameFile) => void
  onDelete: (fileId: string) => void
  onDuplicate: (file: GameFile) => void
  onExport: (file: GameFile) => void
  onImport: () => void
  onStartSession: (file: GameFile) => void
}

export function GameFileManager({
  gameFiles,
  onCreateNew,
  onEdit,
  onDelete,
  onDuplicate,
  onExport,
  onImport,
  onStartSession
}: GameFileManagerProps) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  
  const structuredFiles = gameFiles.filter(f => f.mode === 'structured')
  
  return (
    <div className="space-y-4">
      {/* ツールバー */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">ゲームファイル管理</h2>
        <div className="flex gap-2">
          <Button onClick={onCreateNew} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            新規作成
          </Button>
          <Button onClick={onImport} variant="outline" size="sm">
            <Upload className="w-4 h-4 mr-2" />
            インポート
          </Button>
        </div>
      </div>
      
      {/* ファイルリスト */}
      {structuredFiles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">📁</div>
            <p className="text-gray-600 mb-4">
              保存されたゲームファイルがありません
            </p>
            <Button onClick={onCreateNew}>
              <Plus className="w-4 h-4 mr-2" />
              最初のゲームファイルを作成
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {structuredFiles.map((file) => (
            <Card 
              key={file.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${
                selectedFile === file.id ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedFile(file.id)}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="truncate">{file.name}</span>
                  <span className="text-xs text-gray-500">v{file.version}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600 line-clamp-2">
                  {file.description || 'No description'}
                </p>
                
                {file.structuredMode && (
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>パラメータ: {file.structuredMode.parameters?.length || 0}</div>
                    <div>アクション: {file.structuredMode.actions?.length || 0}</div>
                    <div>フェーズ: {file.structuredMode.phases?.length || 0}</div>
                  </div>
                )}
                
                <div className="text-xs text-gray-400">
                  更新: {new Date(file.metadata.updatedAt).toLocaleDateString()}
                </div>
                
                <div className="flex gap-1 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      onStartSession(file)
                    }}
                  >
                    <Play className="w-3 h-3 mr-1" />
                    プレイ
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit(file)
                    }}
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    編集
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDuplicate(file)
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      onExport(file)
                    }}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (confirm(`"${file.name}"を削除しますか？`)) {
                        onDelete(file.id)
                      }
                    }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}